// src/components/employee/TaskCalendar.jsx

import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Badge,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Modal,
  List,
  Button,
  Space,
  Select,
  message,
  Tooltip
} from 'antd';
import {
  Calendar as CalendarIcon,
  Clock,
  CheckCircle,
  AlertCircle,
  Flag,
  User
} from 'lucide-react';
import moment from 'moment';
import EmployeeTaskService from '../../../services/EmployeeTaskService';
import EmployeeService from '../../../services/EmployeeService';
import { Form, Input } from 'antd';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TaskCalendar = () => {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('month');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [selectedTaskForComment, setSelectedTaskForComment] = useState(null);
  const [commentForm] = Form.useForm();

  useEffect(() => {
    fetchTasks();
    fetchEmployees();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await EmployeeTaskService.getAllTasks();
      setTasks(data);
    } catch (error) {
      message.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await EmployeeService.getAllEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const getEmployeeName = (assignedTo) => {
    if (!assignedTo) return 'Unknown Employee';
    // Support both string ID and object
    let employeeId = typeof assignedTo === 'string' ? assignedTo : assignedTo._id;
    const employee = employees.find(emp => emp._id === employeeId);
    if (employee) {
      // Support both employee.personalInfo and employee.firstName/lastName
      if (employee.personalInfo) {
        return `${employee.personalInfo.firstName} ${employee.personalInfo.lastName}`;
      }
      return `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
    }
    return 'Unknown Employee';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      'in-progress': 'blue',
      completed: 'green',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red'
    };
    return colors[priority] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-4 h-4" />,
      'in-progress': <AlertCircle className="w-4 h-4" />,
      completed: <CheckCircle className="w-4 h-4" />,
      cancelled: <AlertCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed' || status === 'cancelled') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const filterTasksByEmployee = (tasks) => {
    if (filterEmployee === 'all') return tasks;
    // Support both string and object for assignedTo
    return tasks.filter(task => {
      if (!task.assignedTo) return false;
      if (typeof task.assignedTo === 'string') {
        return task.assignedTo === filterEmployee;
      }
      if (typeof task.assignedTo === 'object' && task.assignedTo._id) {
        return task.assignedTo._id === filterEmployee;
      }
      return false;
    });
  };

  const getTasksForDate = (date) => {
    const filteredTasks = filterTasksByEmployee(tasks);
    return filteredTasks.filter(task => {
      if (!task.dueDate) return false;
      return moment(task.dueDate).isSame(date, 'day');
    });
  };

  const getCreatedTasksForDate = (date) => {
    const filteredTasks = filterTasksByEmployee(tasks);
    return filteredTasks.filter(task => {
      return moment(task.createdAt).isSame(date, 'day');
    });
  };

  const getCompletedTasksForDate = (date) => {
    const filteredTasks = filterTasksByEmployee(tasks);
    return filteredTasks.filter(task => {
      return task.completedAt && moment(task.completedAt).isSame(date, 'day');
    });
  };

  const dateCellRender = (value) => {
    const dueTasks = getTasksForDate(value);
    const createdTasks = getCreatedTasksForDate(value);
    const completedTasks = getCompletedTasksForDate(value);

    const totalTasks = dueTasks.length + createdTasks.length + completedTasks.length;
    
    if (totalTasks === 0) return null;

    return (
      <div className="space-y-1">
        {/* Due tasks */}
        {dueTasks.map((task, index) => (
          <div key={`due-${task._id}`} className="text-xs">
            <Badge 
              color={isOverdue(task.dueDate, task.status) ? 'red' : getStatusColor(task.status)}
              text={
                <span className={`text-xs truncate ${isOverdue(task.dueDate, task.status) ? 'text-red-600' : 'text-gray-700'}`}>
                  📅 {task.title}
                </span>
              }
            />
          </div>
        ))}

        {/* Created tasks */}
        {createdTasks.map((task, index) => (
          <div key={`created-${task._id}`} className="text-xs">
            <Badge 
              color="cyan"
              text={
                <span className="text-xs text-gray-700 truncate">
                  ✨ {task.title}
                </span>
              }
            />
          </div>
        ))}

        {/* Completed tasks */}
        {completedTasks.map((task, index) => (
          <div key={`completed-${task._id}`} className="text-xs">
            <Badge 
              color="green"
              text={
                <span className="text-xs text-gray-700 truncate">
                  ✅ {task.title}
                </span>
              }
            />
          </div>
        ))}
      </div>
    );
  };

  const onDateClick = (value) => {
    const dueTasks = getTasksForDate(value);
    const createdTasks = getCreatedTasksForDate(value);
    const completedTasks = getCompletedTasksForDate(value);

    const allDayTasks = [
      ...dueTasks.map(task => ({ ...task, type: 'due' })),
      ...createdTasks.map(task => ({ ...task, type: 'created' })),
      ...completedTasks.map(task => ({ ...task, type: 'completed' }))
    ];

    if (allDayTasks.length > 0) {
      setSelectedDate(value);
      setSelectedTasks(allDayTasks);
      setModalVisible(true);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const completedAt = newStatus === 'completed' ? new Date().toISOString() : null;
      await EmployeeTaskService.updateTaskStatus(taskId, newStatus, completedAt);
      message.success('Task status updated');
      fetchTasks();
    } catch (error) {
      message.error('Failed to update task status');
    }
  };

  const getTaskTypeLabel = (type) => {
    const labels = {
      due: '📅 Due Date',
      created: '✨ Created',
      completed: '✅ Completed'
    };
    return labels[type] || type;
  };

  const getTaskTypeColor = (type) => {
    const colors = {
      due: 'blue',
      created: 'cyan',
      completed: 'green'
    };
    return colors[type] || 'default';
  };

  const handleAddComment = async (values) => {
    try {
      await EmployeeTaskService.addComment(selectedTaskForComment._id, values.comment, null);
      message.success('Comment added successfully');
      setCommentModalVisible(false);
      commentForm.resetFields();
      fetchTasks();
    } catch (error) {
      message.error('Failed to add comment');
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <Title level={2} className="text-gray-800 mb-0">Task Calendar</Title>
          <Space>
            <Select
              placeholder="Filter by Employee"
              className="w-48"
              value={filterEmployee}
              onChange={setFilterEmployee}
            >
              <Option value="all">All Employees</Option>
              {employees.map(employee => (
                <Option key={employee._id} value={employee._id}>
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>
                      {employee.personalInfo
                        ? `${employee.personalInfo.firstName} ${employee.personalInfo.lastName}`
                        : `${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Space>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6">
        <Card title="Legend" size="small" className="bg-white shadow-sm border-0 rounded-lg">
          <Space wrap className="flex flex-wrap gap-2">
            <Tag color="blue" className="flex items-center space-x-1">
              <span>📅 Due Date</span>
            </Tag>
            <Tag color="cyan" className="flex items-center space-x-1">
              <span>✨ Created</span>
            </Tag>
            <Tag color="green" className="flex items-center space-x-1">
              <span>✅ Completed</span>
            </Tag>
            <Tag color="red" className="flex items-center space-x-1">
              <span>🚨 Overdue</span>
            </Tag>
            <Tag color="orange" className="flex items-center space-x-1">
              <span>⏳ Pending</span>
            </Tag>
            <Tag color="blue" className="flex items-center space-x-1">
              <span>🔄 In Progress</span>
            </Tag>
          </Space>
        </Card>
      </div>

      {/* Task Statistics for Current Month */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg text-center">
            <div className="p-2">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {filterTasksByEmployee(tasks).length}
              </div>
              <Text className="text-gray-600 text-sm">Total Tasks</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg text-center">
            <div className="p-2">
              <div className="text-2xl font-bold text-orange-500 mb-1">
                {filterTasksByEmployee(tasks).filter(t => t.status === 'pending').length}
              </div>
              <Text className="text-gray-600 text-sm">Pending</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg text-center">
            <div className="p-2">
              <div className="text-2xl font-bold text-green-500 mb-1">
                {filterTasksByEmployee(tasks).filter(t => t.status === 'completed').length}
              </div>
              <Text className="text-gray-600 text-sm">Completed</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="bg-white shadow-sm border-0 rounded-lg text-center">
            <div className="p-2">
              <div className="text-2xl font-bold text-red-500 mb-1">
                {filterTasksByEmployee(tasks).filter(t => isOverdue(t.dueDate, t.status)).length}
              </div>
              <Text className="text-gray-600 text-sm">Overdue</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Calendar */}
      <Card className="bg-white shadow-sm border-0 rounded-lg">
        <Calendar
          dateCellRender={dateCellRender}
          onSelect={onDateClick}
          mode={viewMode}
          onPanelChange={(value, mode) => setViewMode(mode)}
          className="rounded-lg"
        />
      </Card>

      {/* Task Details Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5" />
            <span>Tasks for {selectedDate.format('MMMM DD, YYYY')}</span>
          </div>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        className="rounded-lg"
      >
        {selectedTasks.length > 0 ? (
          <List
            dataSource={selectedTasks}
            renderItem={task => (
              <List.Item
                actions={[
                  <Select
                    value={task.status}
                    className="w-28"
                    onChange={(value) => handleStatusChange(task._id, value)}
                    size="small"
                  >
                    <Option value="pending">Pending</Option>
                    <Option value="in-progress">In Progress</Option>
                    <Option value="completed">Completed</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>,
                  <Button
                    type="link"
                    onClick={() => {
                      setSelectedTaskForComment(task);
                      setCommentModalVisible(true);
                    }}
                  >
                    Add Comment
                  </Button>
                ]}
                className="px-4 py-3 hover:bg-gray-50 rounded-lg mb-2"
              >
                <List.Item.Meta
                  avatar={
                    <Badge 
                      dot={isOverdue(task.dueDate, task.status)}
                      color={isOverdue(task.dueDate, task.status) ? 'red' : getStatusColor(task.status)}
                      className="flex items-center"
                    >
                      <div className="p-2 bg-blue-100 rounded-full">
                        {getStatusIcon(task.status)}
                      </div>
                    </Badge>
                  }
                  title={
                    <div className="flex items-center space-x-3">
                      <Text strong className="text-gray-900">{task.title}</Text>
                      <Space>
                        <Tag color={getTaskTypeColor(task.type)} className="flex items-center space-x-1">
                          <span>{getTaskTypeLabel(task.type)}</span>
                        </Tag>
                        <Tag color={getStatusColor(task.status)} className="flex items-center space-x-1">
                          {getStatusIcon(task.status)}
                          <span>{task.status.toUpperCase()}</span>
                        </Tag>
                        <Tag color={getPriorityColor(task.priority)} className="flex items-center space-x-1">
                          <Flag className="w-3 h-3" />
                          <span>{(task.priority || 'medium').toUpperCase()}</span>
                        </Tag>
                      </Space>
                    </div>
                  }
                  description={
                    <div className="space-y-2">
                      <div className="text-gray-600">{task.description || 'No description provided'}</div>
                      <div className="flex items-center space-x-4 text-sm">
                        <Text type="secondary" className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{getEmployeeName(task.assignedTo)}</span>
                        </Text>
                        {task.dueDate && (
                          <Text type={isOverdue(task.dueDate, task.status) ? "danger" : "secondary"} className="flex items-center space-x-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span>Due: {moment(task.dueDate).format('MMM DD, YYYY')}</span>
                          </Text>
                        )}
                        {task.completedAt && (
                          <Text type="success" className="flex items-center space-x-1">
                            <CheckCircle className="w-4 h-4" />
                            <span>Completed: {moment(task.completedAt).format('MMM DD, YYYY')}</span>
                          </Text>
                        )}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <div className="text-center py-12">
            <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No tasks for this date</p>
          </div>
        )}
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <span>Add Comment</span>
          </div>
        }
        visible={commentModalVisible}
        onCancel={() => {
          setCommentModalVisible(false);
          commentForm.resetFields();
        }}
        footer={null}
        width={400}
        className="rounded-lg"
      >
        <Form
          form={commentForm}
          layout="vertical"
          onFinish={handleAddComment}
          className="mt-4"
        >
          <Form.Item
            name="comment"
            label={<span className="text-gray-700 font-medium">Comment</span>}
            rules={[{ required: true, message: 'Please enter a comment' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter your comment here..."
              className="rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
            >
              Submit Comment
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TaskCalendar;